
import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, Play, Mic, Sparkles, Smartphone, FileText, RefreshCw, CheckCircle, ChevronDown, Shield, Zap, MousePointer2 } from 'lucide-react';
import { EHRStyle } from './types';
import LegacyRegistration from './views/LegacyRegistration';
import FlowSheet from './views/FlowSheet';
import PostOpLegacy from './views/PostOpLegacy';
import ChartNote from './views/ChartNote';
import EncounterScreens from './views/EncounterScreens';
import { PhoneMockup } from './components/PhoneMockup';
import { SpeechMikeMockup } from './components/SpeechMikeMockup';
import { DesktopMockup } from './components/DesktopMockup';
import demoAudio from './audio/demo_full.wav';

const logoUrl = '/logo.png';

const App: React.FC = () => {
  const [isMobileScreen, setIsMobileScreen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    setIsMobileScreen(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobileScreen(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const liveEnabled = new URLSearchParams(window.location.search).has('live');
  const [currentStyle, setCurrentStyle] = useState<EHRStyle>(EHRStyle.CHART_NOTE);
  const [isDictating, setIsDictating] = useState(false);
  const dictationIntervalRef = useRef<number | null>(null);
  const recognitionRef = useRef<any>(null);
  const demoAbortRef = useRef<AbortController | null>(null);

  // Demo State
  const [demoValues, setDemoValues] = useState({
    description: '',
    severity: '',
    date: '',
    time: '',
    note: '',
    focusedField: null as string | null,
    mouseClickTarget: null as string | null,
  });
  const [demoText, setDemoText] = useState('');
  const [isDemoRunning, setIsDemoRunning] = useState(false);
  const [isQuiet, setIsQuiet] = useState(false);
  const [phoneOverlayVisible, setPhoneOverlayVisible] = useState(true);
  const [showPostDemo, setShowPostDemo] = useState(false);
  const [isLiveTest, setIsLiveTest] = useState(false);
  const [showIntroBubble, setShowIntroBubble] = useState(false);
  const [showDemoChooser, setShowDemoChooser] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [micModality, setMicModality] = useState<'phone' | 'speechmike'>('phone');
  const skipIntroRef = useRef<(() => void) | null>(null);

  // Chart Note Demo State
  const [chartNoteDemoValues, setChartNoteDemoValues] = useState({
    note: '',
    medications: '',
    homeMeds: '',
    allergies: '',
    labs: '',
    focusedField: null as string | null,
    mouseClickTarget: null as string | null,
  });
  const [isChartNoteDemoRunning, setIsChartNoteDemoRunning] = useState(false);
  const chartNoteDemoAudioRef = useRef<HTMLAudioElement | null>(null);

  // Keep track of isQuiet in a ref so the async step function always uses the latest value
  const isQuietRef = useRef(isQuiet);
  useEffect(() => {
    isQuietRef.current = isQuiet;
  }, [isQuiet]);

  useEffect(() => {
    if (isAuthenticated) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [isAuthenticated]);

  const demoAudioRef = useRef<HTMLAudioElement | null>(null);

  const parseStepAction = (actionStr: string): Record<string, string> => {
    const updates: Record<string, string> = {};
    for (const pair of actionStr.split('|')) {
      const colonIdx = pair.indexOf(':');
      if (colonIdx === -1) continue;
      const key = pair.substring(0, colonIdx);
      const value = pair.substring(colonIdx + 1);
      if (key) updates[key] = value;
    }
    return updates;
  };

  const startDemo = async () => {
    if (isDemoRunning) return;

    // Create abort controller for this demo run
    demoAbortRef.current?.abort();
    const abortController = new AbortController();
    demoAbortRef.current = abortController;
    const signal = abortController.signal;

    try {
    document.getElementById('demo-area')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setPhoneOverlayVisible(false);
    setShowPostDemo(false);
    setShowDemoChooser(false);
    setIsLiveTest(false);
    setIsDemoRunning(true);
    setCurrentStyle(EHRStyle.ENCOUNTER_SCREENS);

    // Reset
    setDemoValues({ description: '', severity: '', date: '', time: '', note: '', focusedField: null, mouseClickTarget: null });
    setDemoText('');

    const wait = (ms: number) => new Promise<void>((resolve, reject) => {
      if (signal.aborted) { reject(new DOMException('Aborted', 'AbortError')); return; }
      const timer = setTimeout(resolve, ms);
      signal.addEventListener('abort', () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); }, { once: true });
    });

    // Show intro bubble — wait 10s or until skip button clicked
    setShowIntroBubble(true);
    await new Promise<void>(resolve => {
      skipIntroRef.current = resolve;
      setTimeout(resolve, 10000);
    });
    skipIntroRef.current = null;
    setShowIntroBubble(false);
    await wait(500);

    // Fetch timeline at runtime so edits take effect without rebuild
    const demoTimeline = await fetch('/scripts/demo-timeline.json').then(r => r.json());

    // Start playing the single audio file
    const audio = new Audio(demoAudio);
    audio.volume = isQuietRef.current ? 0 : 1.0;
    demoAudioRef.current = audio;

    // Update volume in real-time when mute toggles
    const volumeInterval = setInterval(() => {
      if (demoAudioRef.current) {
        demoAudioRef.current.volume = isQuietRef.current ? 0 : 1.0;
      }
    }, 100);

    // Wait for audio to actually start playing before running the timeline
    await new Promise<void>((resolve, reject) => {
      audio.onplaying = () => resolve();
      audio.onerror = () => reject(new Error('Audio failed to load'));
      audio.play().catch(reject);
    });

    // Set initial focus
    setDemoValues(prev => ({ ...prev, focusedField: 'description' }));

    const segments = demoTimeline.segments;
    const audioStartTime = performance.now();

    for (let i = 0; i < segments.length; i++) {
      if (signal.aborted) break;
      const seg = segments[i];

      // Wait until this segment's start time, synced to actual audio playback
      const elapsed = performance.now() - audioStartTime;
      const waitUntilStart = Math.max(0, seg.startMs - elapsed);
      if (waitUntilStart > 0) await wait(waitUntilStart);

      // Show speech bubble and dictating state (mouse click segments don't show dictating)
      const isMouseClick = seg.stepAction.includes('mouseClickTarget:');
      setDemoText(seg.stepLabel);
      if (!isMouseClick) setIsDictating(true);

      const updates = parseStepAction(seg.stepAction);
      const segDuration = seg.endMs - seg.startMs;

      // For dictation note segments, progressively type out words
      if (seg.id.startsWith('dictation_note') && (updates.note || updates.note_append)) {
        const text = updates.note || updates.note_append || '';
        const isAppend = !!updates.note_append;
        const words = text.split(/(\s+)/).filter((w: string) => w.trim().length > 0);
        const transcriptionDelay = 2400;
        // Build chunks of 2-4 words
        const chunks: string[] = [];
        let i = 0;
        while (i < words.length) {
          const size = 2 + Math.floor(Math.random() * 3); // 2-4 words
          chunks.push(words.slice(i, i + size).join(' '));
          i += size;
        }
        const msPerChunk = segDuration / chunks.length;
        const chunkStartTime = performance.now();
        let built = '';
        for (let c = 0; c < chunks.length; c++) {
          const targetTime = chunkStartTime + ((c + 1) * msPerChunk) + transcriptionDelay;
          const delay = Math.max(0, targetTime - performance.now());
          if (delay > 0) await wait(delay);
          if (isAppend) {
            const chunk = (built || c > 0 ? ' ' : '') + chunks[c];
            built += chunk;
            setDemoValues(prev => ({ ...prev, note: prev.note + chunk, focusedField: 'note' }));
          } else {
            built = built ? built + ' ' + chunks[c] : chunks[c];
            setDemoValues(prev => ({ ...prev, note: built, focusedField: 'note' }));
          }
        }
        setIsDictating(false);
      } else if (updates.note_append) {
        // For non-dictation appends (like new paragraph)
        setDemoValues(prev => ({ ...prev, note: prev.note + updates.note_append }));
        const elapsedNow = performance.now() - audioStartTime;
        const waitUntilEnd = Math.max(0, seg.endMs - elapsedNow);
        await wait(waitUntilEnd);
        setIsDictating(false);
      } else if (updates.note_correct) {
        // Auto-correction: replace a misspelled word silently
        const [wrong, right] = updates.note_correct.split(':');
        await wait(600);
        setDemoValues(prev => ({ ...prev, note: prev.note.replace(wrong, right) }));
        const elapsedNow = performance.now() - audioStartTime;
        await wait(Math.max(0, seg.endMs - elapsedNow));
        setIsDictating(false);
      } else {
        // Wait until segment ends
        const elapsedNow = performance.now() - audioStartTime;
        const waitUntilEnd = Math.max(0, seg.endMs - elapsedNow);
        await wait(waitUntilEnd);

        // Apply the UI updates
        setIsDictating(false);
        setDemoValues(prev => ({ ...prev, ...updates }));
      }
    }

    clearInterval(volumeInterval);
    if (!signal.aborted) {
      await wait(1000).catch(() => {});
      demoAudioRef.current = null;
      setIsDemoRunning(false);
      setDemoText('');
      setDemoValues(prev => ({ ...prev, mouseClickTarget: null }));
      setShowPostDemo(true);
    }
  } catch (e: any) {
    if (e?.name === 'AbortError') {
      // Demo was stopped by user — cleanup handled by stopDemo
      return;
    }
    throw e;
  }
  };

  const startChartNoteDemo = async () => {
    if (isChartNoteDemoRunning || isDemoRunning) return;

    // Create abort controller for this demo run
    demoAbortRef.current?.abort();
    const abortController = new AbortController();
    demoAbortRef.current = abortController;
    const signal = abortController.signal;

    try {
    document.getElementById('demo-area')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setPhoneOverlayVisible(false);
    setShowPostDemo(false);
    setShowDemoChooser(false);
    setIsLiveTest(false);
    setIsChartNoteDemoRunning(true);
    setCurrentStyle(EHRStyle.CHART_NOTE);

    // Reset chart note demo values
    setChartNoteDemoValues({ note: '', medications: '', homeMeds: '', allergies: '', labs: '', focusedField: null, mouseClickTarget: null });
    setDemoText('');

    const wait = (ms: number) => new Promise<void>((resolve, reject) => {
      if (signal.aborted) { reject(new DOMException('Aborted', 'AbortError')); return; }
      const timer = setTimeout(resolve, ms);
      signal.addEventListener('abort', () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); }, { once: true });
    });

    // Show intro bubble
    setShowIntroBubble(true);
    await new Promise<void>(resolve => {
      skipIntroRef.current = resolve;
      setTimeout(resolve, 10000);
    });
    skipIntroRef.current = null;
    setShowIntroBubble(false);
    await wait(500);

    // Fetch chart note timeline
    const timeline = await fetch('/scripts/chart-note-timeline.json').then(r => r.json());

    // Start audio
    const audio = new Audio('/audio/chart_note_demo.wav');
    audio.volume = isQuietRef.current ? 0 : 1.0;
    chartNoteDemoAudioRef.current = audio;

    const volumeInterval = setInterval(() => {
      if (chartNoteDemoAudioRef.current) {
        chartNoteDemoAudioRef.current.volume = isQuietRef.current ? 0 : 1.0;
      }
    }, 100);

    // Try to play audio — if it fails (file not yet recorded), continue without it
    let audioStartTime = performance.now();
    try {
      await new Promise<void>((resolve, reject) => {
        audio.onplaying = () => resolve();
        audio.onerror = () => resolve(); // gracefully continue if audio missing
        audio.play().catch(() => resolve());
        setTimeout(resolve, 2000); // fallback timeout
      });
    } catch {
      // continue without audio
    }
    audioStartTime = performance.now();

    const segments = timeline.segments;

    for (let i = 0; i < segments.length; i++) {
      if (signal.aborted) break;
      const seg = segments[i];

      const elapsed = performance.now() - audioStartTime;
      const waitUntilStart = Math.max(0, seg.startMs - elapsed);
      if (waitUntilStart > 0) await wait(waitUntilStart);

      setDemoText(seg.stepLabel);
      setIsDictating(true);

      const action = seg.stepAction as string;
      const colonIdx = action.indexOf(':');
      const key = action.substring(0, colonIdx);
      const value = action.substring(colonIdx + 1);
      const segDuration = seg.endMs - seg.startMs;

      if (key === 'note') {
        // Progressive chunk-by-chunk (2-4 words) typing for main note (HPI)
        const words = value.split(/(\s+)/).filter((w: string) => w.trim().length > 0);
        const transcriptionDelay = 1800;
        const chunks: string[] = [];
        let ni = 0;
        while (ni < words.length) {
          const sz = 2 + Math.floor(Math.random() * 3);
          chunks.push(words.slice(ni, ni + sz).join(' '));
          ni += sz;
        }
        const msPerChunk = segDuration / chunks.length;
        const chunkStartTime = performance.now();
        let built = '';
        for (let c = 0; c < chunks.length; c++) {
          const targetTime = chunkStartTime + ((c + 1) * msPerChunk) + transcriptionDelay;
          const delay = Math.max(0, targetTime - performance.now());
          if (delay > 0) await wait(delay);
          built = built ? built + ' ' + chunks[c] : chunks[c];
          const typed = built;
          setChartNoteDemoValues(prev => ({ ...prev, note: typed, focusedField: 'note' }));
        }
        setIsDictating(false);
      } else if (key === 'note_append') {
        // Append to note (new paragraph, macro, assessment)
        const isNewParagraph = value === '\n\n';
        if (isNewParagraph) {
          setChartNoteDemoValues(prev => ({ ...prev, note: prev.note + value, focusedField: 'note' }));
          const elapsedNow = performance.now() - audioStartTime;
          await wait(Math.max(0, seg.endMs - elapsedNow));
          setIsDictating(false);
        } else {
          // Progressive chunk-by-chunk (2-4 words) for appended text.
          const words = value.split(/(\s+)/).filter((w: string) => w.trim().length > 0);
          const requestedDelay = seg.id === 'dictation_macro' ? 2800 : seg.id === 'dictation_assessment' ? 2800 : 800;
          const initialDelay = Math.min(requestedDelay, segDuration - 300);
          // Build chunks of 2-4 words
          const chunks: string[] = [];
          let ci = 0;
          while (ci < words.length) {
            const size = 2 + Math.floor(Math.random() * 3);
            chunks.push(words.slice(ci, ci + size).join(' '));
            ci += size;
          }
          const msPerChunk = Math.max(50, (segDuration - initialDelay) / chunks.length);
          let baseNote = '';
          setChartNoteDemoValues(prev => { baseNote = prev.note; return prev; });
          await wait(initialDelay);
          const chunkStartTime = performance.now();
          let built = '';
          for (let c = 0; c < chunks.length; c++) {
            const targetTime = chunkStartTime + ((c + 1) * msPerChunk);
            const delay = Math.max(0, targetTime - performance.now());
            if (delay > 0) await wait(delay);
            built = built ? built + ' ' + chunks[c] : chunks[c];
            setChartNoteDemoValues(prev => ({ ...prev, note: baseNote + built, focusedField: 'note' }));
          }
          setIsDictating(false);
        }
      } else if (key === 'contextual_edit') {
        // Brief pause to simulate processing, then replace note content with edited version
        await wait(2800);
        setChartNoteDemoValues(prev => ({ ...prev, note: prev.note.replace(
          /Assessment and Plan:[\s\S]*/,
          value
        ), focusedField: 'note' }));
        const elapsedNow = performance.now() - audioStartTime;
        await wait(Math.max(0, seg.endMs - elapsedNow));
        setIsDictating(false);
      } else if (['medications','medications_append','homeMeds','homeMeds_append','allergies','allergies_append','labs','labs_append'].includes(key)) {
        const initialFieldDelay = key.startsWith('labs') ? 2800 : key.startsWith('allergies') ? 2800 : 4400;
        // Sidebar field: wait initial delay then inject text in 2-4 word chunks,
        // then auto-correct spoken full words to medical abbreviations
        const abbreviations: Record<string,string> = {
          'Three Times Daily': 'TID', 'Three times daily': 'TID',
          'Twice Daily': 'BID', 'Twice daily': 'BID',
          'At Bedtime': 'QHS', 'At bedtime': 'QHS',
          'Once Daily': 'QD', 'Once daily': 'QD',
          'Four Times Daily': 'QID', 'Four times daily': 'QID',
        };
        const fieldMap: Record<string,string> = { medications:'medications', medications_append:'medications', homeMeds:'homeMeds', homeMeds_append:'homeMeds', allergies:'allergies', allergies_append:'allergies', labs:'labs', labs_append:'labs' };
        const isAppend = key.endsWith('_append');
        const field = fieldMap[key];
        await wait(initialFieldDelay);
        const sWords = value.split(' ').filter(Boolean);
        const sChunks: string[] = [];
        let si = 0;
        while (si < sWords.length) {
          const sz = 2 + Math.floor(Math.random() * 3);
          sChunks.push(sWords.slice(si, si + sz).join(' '));
          si += sz;
        }
        let sBuilt = '';
        for (let sc = 0; sc < sChunks.length; sc++) {
          if (sc > 0) await wait(350);
          const newChunk = sChunks[sc];
          if (isAppend) {
            const prefix = sc === 0 ? '' : ' ';
            setChartNoteDemoValues(prev => ({ ...prev, [field]: (prev[field as keyof typeof prev] as string) + prefix + newChunk, focusedField: field }));
          } else {
            sBuilt = sBuilt ? sBuilt + ' ' + newChunk : newChunk;
            const v = sBuilt;
            setChartNoteDemoValues(prev => ({ ...prev, [field]: v, focusedField: field }));
          }
        }
        // Auto-correct spoken full words to medical abbreviations
        const hasAbbrev = Object.keys(abbreviations).some(k => value.includes(k));
        if (hasAbbrev) {
          await wait(300);
          setChartNoteDemoValues(prev => {
            let corrected = prev[field as keyof typeof prev] as string;
            for (const [full, abbr] of Object.entries(abbreviations)) {
              corrected = corrected.replace(full, abbr);
            }
            return { ...prev, [field]: corrected, focusedField: field };
          });
        }
        const elapsedNow = performance.now() - audioStartTime;
        await wait(Math.max(0, seg.endMs - elapsedNow));
        setIsDictating(false);
      } else if (key === 'medications_newline') {
        await wait(400);
        setChartNoteDemoValues(prev => ({ ...prev, medications: prev.medications + '\n', focusedField: 'medications' }));
        const elapsedNow = performance.now() - audioStartTime;
        await wait(Math.max(0, seg.endMs - elapsedNow));
        setIsDictating(false);
      } else if (key === 'homeMeds_newline') {
        await wait(400);
        setChartNoteDemoValues(prev => ({ ...prev, homeMeds: prev.homeMeds + '\n', focusedField: 'homeMeds' }));
        const elapsedNow = performance.now() - audioStartTime;
        await wait(Math.max(0, seg.endMs - elapsedNow));
        setIsDictating(false);
      } else if (key === 'allergies_newline') {
        await wait(400);
        setChartNoteDemoValues(prev => ({ ...prev, allergies: prev.allergies + '\n', focusedField: 'allergies' }));
        const elapsedNow = performance.now() - audioStartTime;
        await wait(Math.max(0, seg.endMs - elapsedNow));
        setIsDictating(false);
      } else if (key === 'labs_newline') {
        await wait(400);
        setChartNoteDemoValues(prev => ({ ...prev, labs: prev.labs + '\n', focusedField: 'labs' }));
        const elapsedNow = performance.now() - audioStartTime;
        await wait(Math.max(0, seg.endMs - elapsedNow));
        setIsDictating(false);
      } else if (key === 'labs_append') {
        await wait(4400);
        setChartNoteDemoValues(prev => ({ ...prev, labs: prev.labs + value, focusedField: 'labs' }));
        const elapsedNow = performance.now() - audioStartTime;
        await wait(Math.max(0, seg.endMs - elapsedNow));
        setIsDictating(false);
      } else if (key === 'note_flow_complete') {
        // Flow Mode complete — replace Physical Exam template blanks with AI-filled values
        await wait(1000);
        setChartNoteDemoValues(prev => ({
          ...prev,
          note: prev.note.replace(/Physical Exam:[\s\S]*$/, value),
          focusedField: 'note',
        }));
        const elapsedNow = performance.now() - audioStartTime;
        await wait(Math.max(0, seg.endMs - elapsedNow));
        setIsDictating(false);
      } else if (key === 'mouseClickTarget') {
        setIsDictating(false);
        setChartNoteDemoValues(prev => ({ ...prev, mouseClickTarget: value }));
        const elapsedNow = performance.now() - audioStartTime;
        await wait(Math.max(0, seg.endMs - elapsedNow));
        setChartNoteDemoValues(prev => ({ ...prev, mouseClickTarget: null }));
      } else {
        // focusedField and other simple actions
        const elapsedNow = performance.now() - audioStartTime;
        await wait(Math.max(0, seg.endMs - elapsedNow));
        setIsDictating(false);
        setChartNoteDemoValues(prev => ({ ...prev, focusedField: value }));
      }
    }

    clearInterval(volumeInterval);
    if (!signal.aborted) {
      await wait(1000).catch(() => {});
      chartNoteDemoAudioRef.current = null;
      setIsChartNoteDemoRunning(false);
      setDemoText('');
      setShowPostDemo(true);
    }
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        // Demo was stopped by user — cleanup handled by stopDemo
        return;
      }
      throw e;
    }
  };

  const handleStartLive = () => {
    setPhoneOverlayVisible(false);
    setIsLiveTest(true);
    setCurrentStyle(EHRStyle.CHART_NOTE);
    
    // Wait for render, then focus and start dictating
    setTimeout(() => {
      const chartNoteEditor = document.getElementById('chart-note-editor');
      if (chartNoteEditor) {
        chartNoteEditor.focus();
      }
      handleDictateStart();
    }, 100);
  };

  const sampleTexts = [
    "Patient presents with a 3-day history of mild headache and fatigue. ",
    "Denies any nausea, vomiting, or visual changes. ",
    "Vitals are stable. ",
    "Will recommend over-the-counter analgesics and rest. ",
    "Follow up in one week if symptoms persist. "
  ];
  const [textIndex, setTextIndex] = useState(0);

  const getEditableElements = () => {
    return Array.from(document.querySelectorAll('[contenteditable="true"], [contenteditable=""], input[type="text"], textarea')) as HTMLElement[];
  };

  const insertTextAtCursor = (text: string) => {
    const el = document.activeElement as HTMLElement;
    if (!el || !getEditableElements().includes(el)) {
      // If no editable element is focused, focus the first one
      const elements = getEditableElements();
      if (elements.length > 0) {
        elements[0].focus();
      } else {
        return;
      }
    }

    const activeEl = document.activeElement as HTMLElement;
    
    if (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA') {
      const input = activeEl as HTMLInputElement | HTMLTextAreaElement;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const value = input.value;
      input.value = value.substring(0, start) + text + value.substring(end);
      input.selectionStart = input.selectionEnd = start + text.length;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    } else if (activeEl.isContentEditable) {
      document.execCommand('insertText', false, text);
    }
  };

  useEffect(() => {
    // Only initialize SpeechRecognition in live mode (?live URL param)
    if (!liveEnabled) return;

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event: any) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          }
        }
        if (finalTranscript) {
          insertTextAtCursor(finalTranscript + ' ');
        }
      };

      recognitionRef.current.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsDictating(false);
      };

      recognitionRef.current.onend = () => {
        // Only restart if we're still supposed to be dictating
        // But for "hold to dictate", we stop on mouse up
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const handleNextField = () => {
    const elements = getEditableElements();
    if (elements.length === 0) return;
    
    const activeElement = document.activeElement as HTMLElement;
    const currentIndex = elements.indexOf(activeElement);
    
    let nextIndex = currentIndex + 1;
    if (nextIndex >= elements.length) {
      nextIndex = 0;
    }
    
    elements[nextIndex].focus();
  };

  const handlePrevField = () => {
    const elements = getEditableElements();
    if (elements.length === 0) return;
    
    const activeElement = document.activeElement as HTMLElement;
    const currentIndex = elements.indexOf(activeElement);
    
    let prevIndex = currentIndex - 1;
    if (prevIndex < 0) {
      prevIndex = elements.length - 1;
    }
    
    elements[prevIndex].focus();
  };

  const simulateTyping = () => {
    const textToType = sampleTexts[textIndex % sampleTexts.length];
    setTextIndex(prev => prev + 1);

    let charIndex = 0;
    
    if (dictationIntervalRef.current) {
      window.clearInterval(dictationIntervalRef.current);
    }

    dictationIntervalRef.current = window.setInterval(() => {
      if (charIndex < textToType.length) {
        const char = textToType.charAt(charIndex);
        insertTextAtCursor(char);
        charIndex++;
      } else {
        if (dictationIntervalRef.current) {
          window.clearInterval(dictationIntervalRef.current);
          dictationIntervalRef.current = null;
        }
        setIsDictating(false);
      }
    }, 30); // Typing speed
  };

  const handleDictateStart = (e?: React.MouseEvent | React.TouchEvent) => {
    e?.preventDefault(); // Prevent button from stealing focus
    setIsDictating(true);
    
    if (currentStyle === EHRStyle.ENCOUNTER_SCREENS) {
      const dictationNote = document.getElementById('dictation-note');
      if (dictationNote) {
        dictationNote.focus();
      }
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.start();
      } catch (e) {
        console.error('Failed to start recognition:', e);
        // Fallback to simulation if speech fails
        simulateTyping();
      }
    } else {
      // Fallback to simulation if no speech API
      simulateTyping();
    }
  };

  const stopDemo = () => {
    // Abort any running demo async loops
    if (demoAbortRef.current) {
      demoAbortRef.current.abort();
      demoAbortRef.current = null;
    }

    // Stop demo audio
    if (demoAudioRef.current) {
      demoAudioRef.current.pause();
      demoAudioRef.current.currentTime = 0;
      demoAudioRef.current = null;
    }
    if (chartNoteDemoAudioRef.current) {
      chartNoteDemoAudioRef.current.pause();
      chartNoteDemoAudioRef.current.currentTime = 0;
      chartNoteDemoAudioRef.current = null;
    }

    // Reset all demo state
    setIsDemoRunning(false);
    setIsChartNoteDemoRunning(false);
    setIsDictating(false);
    setDemoText('');
    setShowIntroBubble(false);
    skipIntroRef.current = null;
    setShowPostDemo(false);

    // Show the initial overlay and demo chooser
    setPhoneOverlayVisible(true);
    setShowDemoChooser(true);
  };

  const handleDictateEnd = () => {
    // If a demo is running, stop it and reset to initial overlay
    if (isDemoRunning || isChartNoteDemoRunning) {
      stopDemo();
      return;
    }

    setIsDictating(false);
    
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }

    if (dictationIntervalRef.current) {
      window.clearInterval(dictationIntervalRef.current);
      dictationIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (dictationIntervalRef.current) {
        window.clearInterval(dictationIntervalRef.current);
      }
    };
  }, []);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput === 'TheRing!') {
      setIsAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password');
      setPasswordInput('');
    }
  };

  if (isMobileScreen) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-8 font-sans text-center">
        <img src={logoUrl} alt="Commure Logo" className="h-10 w-auto object-contain mb-8" />
        <h1 className="text-2xl font-semibold text-slate-900 mb-3">Commure Dictation is designed for your Windows PC.</h1>
        <p className="text-slate-500 text-base mb-10 max-w-sm leading-relaxed">Please view this page on a desktop or laptop screen for the full demo experience.</p>
        <div className="border-t border-slate-200 pt-8 w-full max-w-sm">
          <p className="text-slate-500 text-sm mb-4">Looking for Commure Scribe on mobile?</p>
          <a
            href="https://getscribe.commure.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition-colors text-sm"
          >
            Go to Commure Scribe →
          </a>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center font-sans">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-slate-100">
          <div className="flex justify-center mb-8">
            <img src={logoUrl} alt="Commure Logo" className="h-8 w-auto object-contain" />
          </div>
          <h2 className="text-2xl font-semibold text-center text-slate-900 mb-6">Enter Password</h2>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                placeholder="Password"
                autoFocus
              />
              {passwordError && (
                <p className="text-red-500 text-sm mt-2">{passwordError}</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full bg-blue-600 text-white font-medium py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Unlock
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col font-sans text-slate-900 relative overflow-x-hidden">
      {/* Background Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-cyan-100/50 rounded-full blur-[120px] -z-10 pointer-events-none"></div>

      {/* Marketing Header */}
      <header className="px-8 py-6 relative z-10">
        <div className="max-w-7xl mx-auto w-full flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img src={logoUrl} alt="Commure Logo" className="h-8 w-auto object-contain" />
          </div>
          <button className="bg-slate-900 text-white px-5 py-2.5 rounded-full font-medium hover:bg-slate-800 transition-colors">
            Get Demo
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-10 flex flex-col items-center relative z-10">
        
        {/* Hero Section */}
        <div className="text-center max-w-3xl mb-12">
          <p className="text-sm font-semibold uppercase tracking-widest text-blue-600 mb-4">Introducing Commure Dictation</p>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6 leading-[1.1]">
            Clinical Documentation<br />at the Speed of Speech.
          </h1>
          <p className="text-xl text-slate-500 leading-relaxed max-w-2xl mx-auto mb-8">
            Windows and VDI Sessions - Type 3x faster with your voice using Commure Dictation - private, clinical dictation that works in every app on your PC.
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => {
                document.getElementById('demo-area')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setShowDemoChooser(true);
                setShowPostDemo(false);
                setPhoneOverlayVisible(true);
              }}
              disabled={isDemoRunning || isChartNoteDemoRunning}
              className="bg-white text-blue-600 border-2 border-blue-600 px-8 py-3.5 rounded-full font-medium hover:bg-blue-50 transition-colors flex items-center text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Play className="w-5 h-5 mr-3" />
              View Demo
            </button>
            <button className="bg-blue-600 text-white px-8 py-3.5 rounded-full font-medium hover:bg-blue-700 transition-colors flex items-center shadow-lg shadow-blue-600/20 text-lg">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 88 88" className="w-5 h-5 mr-3 fill-current">
                <path d="M0 12.402l35.687-4.86.016 34.423-35.703.206zm35.67 33.529l.016 34.453L0 76.03V46.135zM40.336 6.36L87.868 0v41.207l-47.532.328zM87.868 46.24V88L40.336 81.33v-35.3z" />
              </svg>
              Download for Windows
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="w-full max-w-4xl mx-auto mb-16 grid grid-cols-3 divide-x divide-slate-100 bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="flex flex-col items-center py-8 px-6 text-center">
            <span className="text-5xl font-bold text-blue-600 tracking-tight">3×</span>
            <span className="text-base font-semibold text-slate-800 mt-2">Faster documentation</span>
            <span className="text-xs text-slate-400 mt-1">vs. keyboard entry</span>
          </div>
          <div className="flex flex-col items-center py-8 px-6 text-center">
            <span className="text-5xl font-bold text-blue-600 tracking-tight">2–4<span className="text-3xl font-semibold"> min</span></span>
            <span className="text-base font-semibold text-slate-800 mt-2">Saved per note</span>
            <span className="text-xs text-slate-400 mt-1">across all EHR text fields</span>
          </div>
          <div className="flex flex-col items-center py-8 px-6 text-center">
            <span className="text-5xl font-bold text-blue-600 tracking-tight">EHR</span>
            <span className="text-base font-semibold text-slate-800 mt-2">Support Included</span>
            <span className="text-xs text-slate-400 mt-1">Epic, Meditech, Athena + more</span>
          </div>
        </div>

        {/* Simulation Area */}
        <div id="demo-area" className="w-full pt-12 flex flex-col lg:flex-row items-start justify-center gap-8 lg:gap-12" style={{ transform: 'scale(0.95)', transformOrigin: 'top center' }}>
          {/* Phone + Chat Bubble Column */}
          <div className="shrink-0 flex flex-col items-center">
            {/* Device Mockup + absolute bubbles */}
            <div className="shrink-0 z-10 flex flex-col items-center relative">
              <div className="h-[780px]" style={{ display: micModality === 'speechmike' ? undefined : 'none' }}>
                <SpeechMikeMockup
                  isDictating={isDictating}
                  showOverlay={phoneOverlayVisible}
                  showPostDemo={showPostDemo}
                  isMuted={isQuiet}
                  onStartDemo={currentStyle === EHRStyle.CHART_NOTE ? startChartNoteDemo : startDemo}
                  onShowChooser={() => { setShowDemoChooser(true); setShowPostDemo(false); }}
                  onToggleMute={() => setIsQuiet(!isQuiet)}
                  onStop={stopDemo}
                />
              </div>
              <div className="h-[780px]" style={{ display: micModality === 'phone' ? undefined : 'none' }}>
                <PhoneMockup
                  isDictating={isDictating}
                  onDictateStart={handleDictateStart}
                  onDictateEnd={handleDictateEnd}
                  onNextField={handleNextField}
                  onPrevField={handlePrevField}
                  showOverlay={phoneOverlayVisible}
                  onStartDemo={currentStyle === EHRStyle.CHART_NOTE ? startChartNoteDemo : startDemo}
                  onShowChooser={() => {
                    setShowDemoChooser(true);
                    setShowPostDemo(false);
                  }}
                  onStartLive={handleStartLive}
                  isMuted={isQuiet}
                  onToggleMute={() => setIsQuiet(!isQuiet)}
                  highlightDictation={showIntroBubble}
                  showDictationArrow={showDemoChooser && !showIntroBubble}
                  showLiveTest={liveEnabled}
                  showPostDemo={showPostDemo}
                />
              </div>

              {/* Commure Assistant Demo Selector — centered in page */}
              {showDemoChooser && !showIntroBubble && (
                <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl p-6 flex flex-col border border-slate-200 z-50 animate-in fade-in slide-in-from-bottom-4 w-[420px]">
                  <div className="flex items-center space-x-3 mb-4">
                    <img src="/logomark.png" alt="Commure" className="w-10 h-10 object-contain" />
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">Commure Assistant — Choose your Demo</span>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed mb-5">
                    Which workflow would you like to see?
                  </p>
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => {
                        setShowDemoChooser(false);
                        setCurrentStyle(EHRStyle.CHART_NOTE);
                        setPhoneOverlayVisible(false);
                        startChartNoteDemo();
                      }}
                      className="flex items-center p-4 rounded-xl border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center mr-4 shrink-0 group-hover:bg-blue-200 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-900 text-sm">Diabetic Follow-up (Chart Note)</span>
                        <span className="text-xs text-slate-500 mt-0.5 leading-snug">Progress note with macros, contextual editing, and sidebar fields</span>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setShowDemoChooser(false);
                        setCurrentStyle(EHRStyle.ENCOUNTER_SCREENS);
                        setPhoneOverlayVisible(false);
                        startDemo();
                      }}
                      className="flex items-center p-4 rounded-xl border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center mr-4 shrink-0 group-hover:bg-blue-200 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-900 text-sm">Clinic Visit (Encounter)</span>
                        <span className="text-xs text-slate-500 mt-0.5 leading-snug">Encounter note with vitals, HPI, and sidebar fields</span>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Dictation Bubble — floats over the phone's card area */}
              {(isDemoRunning || isChartNoteDemoRunning) && demoText && (
                <div className="absolute top-[110px] left-[10px] right-[10px] z-20 bg-white rounded-2xl shadow-2xl border border-slate-100 p-4 flex items-center gap-4 animate-in fade-in duration-150">
                  <div className="relative shrink-0">
                    <img
                      src={isChartNoteDemoRunning ? '/doctor02.png' : '/doctor01.png'}
                      alt="Doctor"
                      className="w-20 h-20 rounded-full border-[3px] border-blue-500 object-cover shadow-md"
                    />
                    <button
                      onClick={() => setIsQuiet(!isQuiet)}
                      className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center text-white transition-all shadow ${isQuiet ? 'bg-slate-400' : 'bg-blue-500 animate-[pulse_1.5s_ease-in-out_infinite]'}`}
                      title={isQuiet ? 'Unmute' : 'Mute'}
                    >
                      {isQuiet ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    {demoText === 'Dictating in Flow Mode...' ? (
                      <>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Dictation</span>
                        <span className="text-sm font-medium text-slate-800 flex items-center gap-2 flex-wrap">
                          Dictating in
                          <span className="inline-flex items-center px-2.5 py-0.5 bg-blue-600 text-white rounded-full text-xs font-semibold">Flow Mode</span>
                        </span>
                      </>
                    ) : ['Next field', 'Previous field', 'New paragraph', 'New line', 'Next line', 'Normal Physical Exam', 'Make this a bulleted list', 'Flow Mode', 'Stop Flow', 'Filling fields...'].includes(demoText) ? (
                      <>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Voice Command</span>
                        <span className="inline-flex self-start px-4 py-1.5 bg-slate-100 text-slate-700 rounded-full text-sm font-semibold border border-slate-200">{demoText}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Dictation</span>
                        <div className="relative pl-6">
                          <span className="absolute top-[-8px] left-0 text-5xl font-serif text-blue-200 leading-none select-none">&ldquo;</span>
                          <span className="text-sm font-medium text-slate-800 leading-snug">{demoText}<span className="text-3xl font-serif text-blue-200 leading-none ml-0.5 align-bottom">&rdquo;</span></span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Intro Bubble — centered in page */}
              {showIntroBubble && (
                <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl p-6 flex items-start space-x-5 border border-slate-200 z-50 animate-in fade-in slide-in-from-bottom-4 w-[420px]">
                  <div className="shrink-0">
                    <img src="/logomark.png" alt="Commure" className="w-12 h-12 object-contain" />
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">Ambient Mobile Mic</span>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      Dictation is built right into <span className="font-semibold text-blue-600">Commure Ambient</span> — just click the <span className="font-semibold">Dictation</span> button in the menu and your phone becomes your mic. Also works with your computer's Mic or <span className="font-semibold text-blue-600">Dictation Speech Mics</span>.
                    </p>
                    <button
                      onClick={() => skipIntroRef.current?.()}
                      className="mt-4 bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors self-start"
                    >
                      Start Demo
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Modality Switcher */}
            <div className="mt-5 flex flex-col items-center gap-3">
              <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">Recording via</span>
              <div className="flex items-center gap-2">
                {/* Phone option */}
                <button
                  onClick={() => setMicModality('phone')}
                  className={`flex flex-col items-center gap-1.5 px-3 py-2 rounded-xl border-2 transition-all ${micModality === 'phone' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                >
                  <svg width="22" height="36" viewBox="0 0 32 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="30" height="50" rx="6" stroke={micModality === 'phone' ? '#1d4ed8' : '#94a3b8'} strokeWidth="2" fill={micModality === 'phone' ? '#eff6ff' : '#f8fafc'}/>
                    <rect x="10" y="3" width="12" height="2" rx="1" fill={micModality === 'phone' ? '#1d4ed8' : '#94a3b8'}/>
                    <circle cx="16" cy="45" r="2.5" stroke={micModality === 'phone' ? '#1d4ed8' : '#94a3b8'} strokeWidth="1.5"/>
                    <rect x="7" y="8" width="18" height="30" rx="2" fill={micModality === 'phone' ? '#dbeafe' : '#e2e8f0'}/>
                  </svg>
                  <span className={`text-[9px] font-semibold ${micModality === 'phone' ? 'text-blue-600' : 'text-slate-400'}`}>Ambient App</span>
                </button>

                <span className="text-slate-300 text-sm font-light">|</span>

                {/* SpeechMike option */}
                <button
                  onClick={() => setMicModality('speechmike')}
                  className={`flex flex-col items-center gap-1.5 px-3 py-2 rounded-xl border-2 transition-all ${micModality === 'speechmike' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                >
                  <svg width="20" height="36" viewBox="0 0 28 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 10 Q6 2 14 2 Q22 2 22 10 L22 42 Q22 50 14 50 Q6 50 6 42 Z" fill={micModality === 'speechmike' ? '#dbeafe' : '#e2e8f0'} stroke={micModality === 'speechmike' ? '#1d4ed8' : '#94a3b8'} strokeWidth="1.5"/>
                    <rect x="9" y="3" width="10" height="8" rx="3" fill={micModality === 'speechmike' ? '#93c5fd' : '#cbd5e1'}/>
                    <rect x="9" y="10" width="10" height="2" rx="1" fill="#ef4444"/>
                    <rect x="8" y="16" width="12" height="4" rx="2" fill={micModality === 'speechmike' ? '#93c5fd' : '#cbd5e1'}/>
                    <rect x="8" y="22" width="5" height="4" rx="2" fill={micModality === 'speechmike' ? '#93c5fd' : '#cbd5e1'}/>
                    <rect x="15" y="22" width="5" height="4" rx="2" fill={micModality === 'speechmike' ? '#93c5fd' : '#cbd5e1'}/>
                    <circle cx="14" cy="33" r="5" fill={micModality === 'speechmike' ? '#bfdbfe' : '#e2e8f0'} stroke={micModality === 'speechmike' ? '#1d4ed8' : '#94a3b8'} strokeWidth="1.5"/>
                    <circle cx="14" cy="33" r="2" fill={micModality === 'speechmike' ? '#1d4ed8' : '#94a3b8'}/>
                    <line x1="9" y1="40" x2="19" y2="40" stroke={micModality === 'speechmike' ? '#93c5fd' : '#cbd5e1'} strokeWidth="1" strokeDasharray="2 2"/>
                    <line x1="9" y1="43" x2="19" y2="43" stroke={micModality === 'speechmike' ? '#93c5fd' : '#cbd5e1'} strokeWidth="1" strokeDasharray="2 2"/>
                    <rect x="11" y="48" width="6" height="3" rx="1.5" fill={micModality === 'speechmike' ? '#1d4ed8' : '#94a3b8'}/>
                  </svg>
                  <span className={`text-[9px] font-semibold ${micModality === 'speechmike' ? 'text-blue-600' : 'text-slate-400'}`}>SpeechMike</span>
                </button>
              </div>
            </div>
          </div>

          {/* Desktop Mockup */}
          <div className="flex-1 w-full max-w-4xl lg:sticky lg:top-12 self-start">
            <div className="relative">
            {showIntroBubble && (
              <div className="absolute inset-0 z-10 flex items-start justify-center pt-12 bg-slate-900/70 backdrop-blur-sm rounded-lg">
                <div className="flex flex-col items-center gap-5 text-center px-10">
                  <svg width="40" height="40" viewBox="0 0 88 88" className="fill-white opacity-90">
                    <path d="M0 12.402l35.687-4.86.016 34.423-35.703.206zm35.67 33.529l.016 34.453L0 76.03V46.135zM40.336 6.36L87.868 0v41.207l-47.532.328zM87.868 46.24V88L40.336 81.33v-35.3z"/>
                  </svg>
                  <div>
                    <p className="text-white font-bold text-lg mb-1">Commure Dictation</p>
                    <p className="text-slate-300 text-sm">Windows App · Running on your PC</p>
                  </div>
                  <div className="flex items-center gap-2.5 bg-[#1132ee] px-4 py-2 rounded-full">
                    <div className="flex items-end gap-[2px] h-4">
                      {[0,1,2,3,4].map(i => (
                        <span key={i} className="w-[3px] rounded-full bg-white/90"
                          style={{ height: `${40 + Math.sin(i * 1.2) * 30}%`, animation: `soundWave 0.8s ease-in-out ${i * 0.12}s infinite alternate` }} />
                      ))}
                    </div>
                    <span className="text-white text-xs font-semibold">Starting...</span>
                  </div>
                </div>
              </div>
            )}
            <DesktopMockup>
              {currentStyle === EHRStyle.CHART_NOTE && (
                <ChartNote isLiveTest={isLiveTest} demoValues={isChartNoteDemoRunning ? chartNoteDemoValues : undefined} />
              )}
              {currentStyle === EHRStyle.LEGACY_REGISTRATION && <LegacyRegistration />}
              {currentStyle === EHRStyle.FLOWSHEET && <FlowSheet />}
              {currentStyle === EHRStyle.POST_OP_LEGACY && <PostOpLegacy />}
              {currentStyle === EHRStyle.ENCOUNTER_SCREENS && <EncounterScreens demoValues={demoValues} onReset={() => setDemoValues({ description: '', severity: '', date: '', time: '', note: '', focusedField: null, mouseClickTarget: null })} />}
            </DesktopMockup>
            </div>
          </div>
        </div>

        {/* ── Feature Breakdown Cards ───────────────────────────────────────── */}
        <section className="w-full max-w-5xl mx-auto mt-24 mb-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-slate-900 mb-3">Dictation that works like you do.</h2>
            <p className="text-lg text-slate-500">Real-time transcription, voice commands, contextual editing — in any EHR, any app.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: <Mic className="w-5 h-5" />, title: 'Real-Time Transcription', body: 'Text appears as you speak — no processing delay, no waiting. Medical-grade accuracy optimized for clinical terminology, even in noisy environments.' },
              { icon: <Zap className="w-5 h-5" />, title: 'Contextual Editing by Voice', body: 'Say "Make this a bulleted list" or "Change lisinopril to 20mg." The AI rewrites in place — no selecting, no deleting, no re-dictating.' },
              { icon: <MousePointer2 className="w-5 h-5" />, title: 'Works With Existing Hardware', body: 'Plug in your Nuance PowerMic III, PowerMic 4, or Philips SpeechMike and start dictating immediately. Same buttons, same hand position.' },
              { icon: <Smartphone className="w-5 h-5" />, title: 'Your Phone as a Wireless Mic', body: 'The Dictation tab in Commure Ambient turns your phone into a wireless microphone. Dictate from anywhere in the room — no tethering, no extra hardware.' },
              { icon: <FileText className="w-5 h-5" />, title: 'Macros & Templates', body: 'Say a short phrase, insert an entire template. Placeholders tab through automatically. "Normal Physical Exam" → full structured exam note, ready to fill.' },
              { icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>, title: 'Works in Every EHR', body: 'Epic, Meditech, Athena, NextGen, eClinicalWorks, and more — plus any application with a text field. No integration, no IT project, no waiting.' },
            ].map(({ icon, title, body }) => (
              <div key={title} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex flex-col gap-3 hover:shadow-md transition-shadow">
                <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">{icon}</div>
                <h3 className="font-semibold text-slate-900">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Virtual Desktop Callout ───────────────────────────────────────── */}
        <div className="w-full max-w-5xl mx-auto mb-24">
          <div className="bg-blue-50 border border-blue-100 rounded-2xl px-8 py-5 flex items-start gap-4">
            <svg className="w-6 h-6 text-blue-700 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="13" rx="1"/><path d="M8 21h8M12 17v4"/></svg>
            <div>
              <p className="text-sm font-semibold text-blue-700 mb-1">Running a virtual desktop?</p>
              <p className="text-sm text-blue-600">Commure Dictation includes dedicated Citrix virtual channel integration — the same reliability, inside your VDI session.</p>
            </div>
          </div>
        </div>

        {/* ── Platform Story ────────────────────────────────────────────────── */}
        <section className="w-full max-w-5xl mx-auto mb-24 text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-blue-600 mb-3">The Platform</p>
          <h2 className="text-3xl font-semibold text-slate-900 mb-4">Dictation is just the beginning.</h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-12">Commure Dictation is part of the Commure documentation platform — the same platform that powers Commure Ambient, the AI-generated clinical note solution used in the nation's largest health systems.</p>
          <div className="flex flex-col lg:flex-row gap-0 items-stretch max-w-4xl mx-auto">
            <div className="flex-1 bg-slate-900 border border-slate-800 rounded-2xl lg:rounded-r-none p-8 text-left shadow-lg">
              <video autoPlay muted loop playsInline className="w-full rounded-lg mb-6 border border-slate-700">
                <source src="https://cdn.prod.website-files.com/66fb0f1ec709d05e0d47be37%2F69cffe53d76b28dc95f279e9_compressed_webm.webm" type="video/webm" />
              </video>
              <div className="flex items-center gap-2 mb-3">
                <img src="/logo.png" alt="Commure" className="h-5 w-auto object-contain brightness-0 invert" />
                <span className="font-semibold text-white text-lg" style={{ fontFamily: 'Inter, sans-serif' }}>Dictation</span>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">Point-and-click voice input. You control exactly what gets typed and where — structured fields, quick edits, addendums, macros.</p>
            </div>
            <div className="flex items-center justify-center bg-blue-600 px-5 py-4 lg:py-0 border-y lg:border-y-0 lg:border-x border-blue-500 shrink-0">
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="flex items-center gap-1.5 text-white">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M13 7H1M5 3L1 7L5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 7H13M9 11L13 7L9 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <span className="text-[10px] font-bold text-white uppercase tracking-wider">Shared</span>
                <div className="flex flex-col gap-1 text-xs text-blue-100">
                  <span>Macros</span>
                  <span>Dictionary</span>
                  <span>Preferences</span>
                  <span>Login</span>
                </div>
              </div>
            </div>
            <div className="flex-1 bg-slate-900 border border-slate-800 rounded-2xl lg:rounded-l-none p-8 text-left shadow-lg">
              <video autoPlay muted loop playsInline className="w-full rounded-lg mb-6 border border-slate-700">
                <source src="https://cdn.prod.website-files.com/66fb0f1ec709d05e0d47be37%2F69af440b364863c3ca2e246f_Ambient%20Product%20Page%20-%20Scribing_webm.webm" type="video/webm" />
              </video>
              <div className="mb-3">
                <img src="https://cdn.prod.website-files.com/66fb0f1ec709d05e0d47be37/679410b2725906e5234e98a1_Full%20Logo%20-%20Inverse.png" alt="Commure Ambient AI" className="h-6 w-auto object-contain" />
              </div>
              <p className="text-sm text-slate-300 leading-relaxed mb-4">AI ambient scribe. It listens to your patient encounter and generates a full clinical note — automatically, hands-free.</p>
              <a href="https://commure.com/ambient-ai" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors">
                Learn more about Commure Ambient
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6H10M7 3L10 6L7 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </a>
            </div>
          </div>
          <p className="text-sm text-slate-400 mt-6">One login. One setup. One documentation experience.</p>
        </section>

        {/* ── Social Proof ─────────────────────────────────────────────────── */}
        <section className="w-full max-w-5xl mx-auto mb-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-slate-900 mb-3">Trusted by clinicians</h2>
            <p className="text-lg text-slate-500">What physicians say after switching.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { quote: "I've used Dragon for 15 years. Commure Dictation is faster, and the contextual editing is something Dragon simply can't do. I'm saving at least 30 minutes a day.", name: 'Physician, Internal Medicine', system: 'Large Academic Medical Center' },
              { quote: "The fastest dictation tool I've used in 20 years of practice. It just works — in Epic, in our Citrix environment, everywhere.", name: 'Physician, Family Medicine', system: 'Regional Health System' },
              { quote: "Setup took under 5 minutes. The macros are a game-changer for our standard note templates. Our whole department switched in a week.", name: 'Hospitalist Physician', system: 'Community Hospital System' },
            ].map(({ quote, name, system }) => (
              <div key={name} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex flex-col gap-4">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                  ))}
                </div>
                <p className="text-slate-700 text-sm leading-relaxed flex-1">&ldquo;{quote}&rdquo;</p>
                <div className="pt-2 border-t border-slate-100">
                  <p className="text-sm font-semibold text-slate-800">{name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{system}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Legacy Migration ───────────────────────────────────────────── */}
        <section className="w-full max-w-5xl mx-auto mb-24">
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-10 lg:p-14">
            <div className="max-w-2xl mb-10">
              <p className="text-blue-600 text-sm font-semibold uppercase tracking-widest mb-3">Migration</p>
              <h2 className="text-3xl font-semibold text-slate-900 mb-4">Already using another Legacy Dictation Tool? You're 5 minutes from switching.</h2>
              <p className="text-slate-500 leading-relaxed">If you're using a Nuance PowerMic III, PowerMic 4, or Philips SpeechMike with another dictation tool, plug it into Commure Dictation and start dictating immediately. Same buttons. Same hand position. What's new: <span className="text-slate-900 font-semibold">contextual editing</span> — instead of re-dictating, just say what you want changed.</p>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
                    <th className="text-left py-4 px-6 text-slate-500 font-medium">Capability</th>
                    <th className="text-center py-4 px-6">
                      <div className="flex items-center justify-center gap-2">
                        <img src="/logo.png" alt="Commure" className="h-4 w-auto object-contain" />
                        <span className="text-blue-600 font-bold text-sm">Dictation</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    'Works with PowerMic III / 4',
                    'Works with Philips SpeechMike',
                    'Real-time transcription',
                    'Citrix support',
                    'Contextual editing by voice',
                    'Works in any EHR without integration',
                    'Phone as wireless mic',
                    'Syncs with ambient AI scribe',
                  ].map((label) => (
                    <tr key={label} className="hover:bg-blue-50/40 transition-colors group">
                      <td className="py-3.5 px-6 text-slate-700 font-medium">{label}</td>
                      <td className="py-3.5 px-6 text-center">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 group-hover:bg-blue-700 transition-colors shadow-sm">
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ── Security & IT ─────────────────────────────────────────────────── */}
        <section className="w-full max-w-5xl mx-auto mb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest text-blue-600 mb-3">For IT & Security Teams</p>
              <h2 className="text-3xl font-semibold text-slate-900 mb-4">Built for healthcare IT.</h2>
              <p className="text-slate-500 leading-relaxed mb-6">No drivers. No IT project. No EHR vendor coordination. Clinicians install it themselves in under 5 minutes — and IT doesn't have to touch a thing.</p>
              <a href="https://docs.ambient.commure.com/commure-dictation/introduction#what-is-commure-dictation" target="_blank" rel="noopener noreferrer" className="inline-block bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition-colors text-sm">Review our Technical Documentation</a>
            </div>
            <div className="flex flex-col gap-3">
              {[
                { icon: <Shield className="w-4 h-4" />, label: 'No EHR integration required', sub: 'Text injection works at the OS level, not through EHR APIs' },
                { icon: <CheckCircle className="w-4 h-4" />, label: 'Citrix virtual channel support', sub: 'Reliable text injection inside VDI sessions' },
                { icon: <CheckCircle className="w-4 h-4" />, label: 'Windows 10 / Windows 11', sub: 'Standard enterprise OS support' },
                { icon: <CheckCircle className="w-4 h-4" />, label: 'HIPAA compliant', sub: 'Designed for protected health information' },
                { icon: <CheckCircle className="w-4 h-4" />, label: 'Single sign-on', sub: 'Uses existing Commure account credentials' },
                { icon: <CheckCircle className="w-4 h-4" />, label: 'Low-latency secure cloud', sub: 'Internet-connected transcription over encrypted channels' },
              ].map(({ icon, label, sub }) => (
                <div key={label} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-100 shadow-sm">
                  <div className="w-7 h-7 rounded-lg bg-green-50 text-green-600 flex items-center justify-center shrink-0 mt-0.5">{icon}</div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────────── */}
        <section className="w-full max-w-3xl mx-auto mb-24">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-slate-900 mb-3">Common questions</h2>
          </div>
          <div className="flex flex-col divide-y divide-slate-100 border border-slate-100 rounded-2xl overflow-hidden bg-white shadow-sm">
            {[
              { q: 'Does this require any EHR integration or IT setup?', a: 'No EHR integration is required — Commure Dictation works at the operating system level, injecting text directly at your cursor, just like typing. For smaller practices, clinicians can be up and running in minutes with zero IT involvement. For larger organizations, we support your existing deployment methodology including MDM/endpoint management tools (Intune, SCCM), SSO and enterprise authentication (SAML, Azure AD, Okta), and Citrix virtual desktop environments. No EHR API, no vendor coordination, no custom integration work required.' },
              { q: 'We use Citrix. Will this work in our VDI environment?', a: 'Yes. Commure Dictation includes dedicated Citrix virtual channel integration for reliable text injection inside VDI sessions.' },
              { q: "We're currently using Dragon Medical One. How hard is it to switch?", a: 'If you\'re using a PowerMic III, PowerMic 4, or Philips SpeechMike, plug it in and start dictating. Same buttons, same hand position. Most clinicians are fully productive within one session.' },
              { q: 'Does this work with Epic?', a: 'Yes. Commure Dictation works with Epic, Cerner, Athena, NextGen, eClinicalWorks, and any application with a text field — including custom hospital portals and Windows desktop apps.' },
              { q: "What's the difference between Commure Dictation and Commure Ambient?", a: 'Commure Ambient is an AI ambient scribe — it listens to your encounter and generates a full clinical note. Commure Dictation is point-and-click voice input — you control exactly what gets typed and where. They share your macros, dictionary, and preferences.' },
              { q: 'What microphones are supported?', a: 'Nuance PowerMic III, PowerMic 4, Philips SpeechMike Premium, and Philips SpeechMike III are fully supported. Any USB microphone or your computer\'s built-in mic also works. The Commure Ambient mobile app can also serve as a wireless microphone.' },
              { q: 'Is this HIPAA compliant?', a: 'Yes. Commure Dictation is designed for healthcare environments and handles protected health information in compliance with HIPAA requirements. Contact our team for our full security documentation.' },
            ].map(({ q, a }, i) => (
              <div key={i}>
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-slate-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-medium text-slate-800 pr-4">{q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-sm text-slate-500 leading-relaxed">{a}</div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ── Final Dual-Track CTA ──────────────────────────────────────────── */}
        <section className="w-full max-w-5xl mx-auto mb-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Clinician card */}
            <div className="bg-blue-600 rounded-3xl p-10 flex flex-col">
              <p className="text-blue-200 text-xs font-semibold uppercase tracking-widest mb-3">For Individual Clinicians</p>
              <h3 className="text-2xl font-semibold text-white mb-3">Ready to try it?</h3>
              <p className="text-blue-100 text-sm leading-relaxed mb-8 flex-1">Download Commure Dictation for Windows and start dictating in minutes. Free to try — no credit card required.</p>
              <div>
                <button className="flex items-center gap-2.5 bg-white text-blue-600 px-6 py-3 rounded-full font-semibold hover:bg-blue-50 transition-colors text-sm shadow-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 88 88" className="w-4 h-4 fill-current shrink-0"><path d="M0 12.402l35.687-4.86.016 34.423-35.703.206zm35.67 33.529l.016 34.453L0 76.03V46.135zM40.336 6.36L87.868 0v41.207l-47.532.328zM87.868 46.24V88L40.336 81.33v-35.3z" /></svg>
                  Download for Windows
                </button>
              </div>
            </div>
            {/* Enterprise card */}
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-10 flex flex-col">
              <p className="text-blue-600 text-xs font-semibold uppercase tracking-widest mb-3">For Health Systems</p>
              <h3 className="text-2xl font-semibold text-slate-900 mb-3">Deploying across your organization?</h3>
              <p className="text-slate-500 text-sm leading-relaxed mb-8 flex-1">Talk to our team about enterprise licensing, pilot programs, and rollout support for your health system.</p>
              <div>
                <div className="flex flex-wrap gap-3 mb-4">
                  <button className="bg-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors text-sm">Request a Demo</button>
                  <button className="border border-slate-200 text-slate-700 px-6 py-3 rounded-full font-semibold hover:bg-slate-50 transition-colors text-sm">Contact Sales</button>
                </div>
                <p className="text-slate-400 text-xs">Questions? support@ambient.commure.com · 888-304-0450</p>
              </div>
            </div>
          </div>
        </section>

      </main>
    </div>
  );
};

export default App;
